// Data seam for the operator-console side of concierge messaging: an account
// manager's inbox of client threads. Reuses the ConciergeMessage / ConciergeManager
// types from the client seam.
//
// Wired to the live endpoints:
// - getAdminConciergeThreads() -> GET /admin/concierge                     (thread list)
// - getAdminConciergeThread(id) -> GET /admin/tenants/:id/concierge        (marks client msgs read)
// - sendAdminConciergeMessage(id, body) -> POST /admin/tenants/:id/concierge/messages

import { apiClient } from '../../api/client.js'
import type { ConciergeManager, ConciergeMessage } from '../concierge/concierge-data.js'

export type { ConciergeManager, ConciergeMessage } from '../concierge/concierge-data.js'

/** One row in the AM's thread list: a client tenant with its latest activity. */
export interface AdminConciergeThreadSummary {
  tenantId: string
  tenantName: string
  lastBody: string | null
  lastAt: string | null
  unread: number
}

export interface AdminConciergeThreadData {
  messages: ConciergeMessage[]
  manager: ConciergeManager | null
}

/** The AM's assigned client threads, most-recent first. */
export async function getAdminConciergeThreads(): Promise<AdminConciergeThreadSummary[]> {
  const res = await apiClient.get<{ data: { threads: AdminConciergeThreadSummary[] } }>(
    '/admin/concierge',
  )
  return res.data.data.threads
}

/** Full thread with one client tenant. The GET marks that client's messages read. */
export async function getAdminConciergeThread(tenantId: string): Promise<AdminConciergeThreadData> {
  const res = await apiClient.get<{ data: AdminConciergeThreadData }>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/concierge`,
  )
  return res.data.data
}

/** Send a message to a client tenant. Returns the persisted message. */
export async function sendAdminConciergeMessage(
  tenantId: string,
  body: string,
): Promise<ConciergeMessage> {
  const res = await apiClient.post<{ data: { message: ConciergeMessage } }>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/concierge/messages`,
    { body },
  )
  return res.data.data.message
}

/** Total unread across an AM's threads (drives the admin nav badge). */
export function totalAdminConciergeUnread(threads: AdminConciergeThreadSummary[]): number {
  return threads.reduce((sum, t) => sum + t.unread, 0)
}
